<template>
  <page ref="pageRef">
    <div class="container">
      <div v-if="applications.length > 0" class="applications">
        <ul class="responsive-table">
          <li class="table-row" v-for="application in sortedApplications" :key="application">
            <application-view :application="application" @applicationCancelled="refreshApplications"></application-view>
          </li>
        </ul>
      </div>
      <div v-else class="no-applications">No Applications Associated</div>
      <div class="app-buttons">
        <button class="app-btn" @click="applyForNewLoan">Apply for a New Student Loan</button>
        <button class="other-apps-button app-btn" @click="viewAllApps" v-if="canApplyForNewApps">
          View Other Applications
        </button>
      </div>
    </div>
  </page>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from "vue";
import useApplications from "@/use/application";
import Page from "../components/shared/layout/Page.vue";
import { useRoute } from "vue-router";
import ApplicationView from "@/views/ApplicationView.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { useStore } from "vuex";

export default defineComponent({
  setup() {
    const route = useRoute();
    const store = useStore();
    const { getUserApplications, getUserApplication } = useApplications();

    const canApplyForNewApps = computed<boolean>(() => {
      return (store.state.applicationsModule.creditUnions || []).length <= 1 ? false : true;
    });

    const pageRef = ref<InstanceType<typeof Page> | null>(null);
    const loading = ref<boolean>(true);

    const sortedApplications = computed(() => {
      if (route.query.lender) {
        return store.state.applications?.filter((a: any) => a.subDomain === route.query.lender);
      } else {
        return store.state.applications;
      }
    });

    return {
      getUserApplications,
      getUserApplication,
      route,
      store,
      sortedApplications,
      pageRef,
      loading,
      canApplyForNewApps,
    };
  },
  components: {
    Page,
    ApplicationView,
  },
  data: () => ({
    userApplications: [],
  }),
  computed: {
    ...mapState(["applications", "configuration", "lenderData", "applicationsModule"]),
    ...mapGetters("oidcStore", ["oidcUser"]),
    ...mapGetters("applicationsModule", ["Branding"]),
  },
  methods: {
    ...mapActions(["setApplications"]),
    viewApplication(referenceId: number) {
      const query = this.$route.query as any;
      // redirect to application page
      this.$router.push({ path: `/applications/${referenceId}`, query });
    },
    applyForNewLoan() {
      const lenderid = this.sortedApplications[0]?.lenderId || this.applications[0].lenderId;
      const productTypeId =
        this.sortedApplications[0]?.productTypeId || this.applications[0].productTypeId;
      const programTypeId =
        this.sortedApplications[0]?.programTypeId || this.applications[0].programTypeId;

      window.location.replace(
        this.store.state.borrowerAppUrls?.[`${lenderid}:${programTypeId ?? ""}:${productTypeId}`] ||
          this.store.state.borrowerAppUrl,
      );
    },
    viewAllApps() {
      this.$router.push({
        path: `/applications/lenders`,
      });
    },
    async refreshApplications() {
      this.userApplications = await this.getUserApplications(this.oidcUser.sub);
      this.setApplications(this.userApplications);
    },
  },
  watch: {
    $route() {
      console.log("route changed...");
    },
    loading: {
      handler(value) {
        this.pageRef?.setLoading(value);
      },
      immediate: true,
    },
  },
  async mounted() {
    this.pageRef?.setLoading(true);
    this.loading = true;

    await this.refreshApplications();

    if (this.route.query.lid) {
      this.store.dispatch("applicationsModule/setLenderId", Number(this.route.query.lid));
      await this.store.dispatch("applicationsModule/setCreditUnionColorScheme", this.Branding);
    }

    if ((this.applications || []).length === 1) {
      this.$router.push({
        path: `/applications/${this.applications[0].referenceId}`,
      });
    }

    this.loading = false;
  },
});
</script>

<style lang="scss" scoped>
.container {
  margin-left: 20px;
  padding-left: 10px;
  padding-right: 10px;
  background-color: #eee;
  display: flex;
  flex-direction: row;
  gap: 15px;
}
.applications {
  flex: 1 1 70%;
  .responsive-table {
    padding-left: 0px;

    ul {
      margin: 0;
    }
    li {
      border-radius: 3px;
      display: flex;
      justify-content: space-between;
      margin-bottom: 30px;
      max-width: 617px;
    }
    .table-header {
      // background-color: #95a5a6;
      font-weight: 700;
      font-size: 1.25rem;
      margin-bottom: 1rem;

      p {
        margin-bottom: 0;
      }
    }
    .table-row {
      box-shadow: 0px 0px 9px 0px rgba(0, 0, 0, 0.1);
    }
    .col-1 {
      flex-basis: 10%;
    }
    .col-2 {
      flex-basis: 40%;
    }
    .col-3 {
      flex-basis: 25%;
    }
    .col-4 {
      flex-basis: 25%;
    }
  }
}
.no-applications {
  font-weight: 700;
  font-size: 1.25rem;
  margin-bottom: 1rem;
  margin-top: 1rem;
  text-align: center;
}
@media (max-width: 991px) {
  .table-header {
    display: none;
  }
  .table-row {
  }
  .col {
    flex-basis: 100%;
  }
}
.app-buttons {
  width: 302px;
  height: fit-content;
  border-radius: 6px;
  background: #fff;
  padding: 30px;
  margin-right: 40px;
  position: relative;
  z-index: 2;
}
.app-btn {
  width: 242px;
  height: 34px;
  border-radius: 4px;
  background: var(--theme-color, #f7941e);
  color: #ffffff;
  font-family: "Montserrat", arial, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
}
.other-apps-button {
  margin-top: 20px;
}

@media (max-width: 992px) {
  .container {
    flex-wrap: wrap;
    padding: 0 !important;
    margin: 0;
  }

  .app-buttons {
    order: 1;
  }

  .applications {
    flex: 1;
    width: 100%;
    order: 2;
  }

  .applications .responsive-table {
    overflow-x: auto;
  }

  .applications .responsive-table li {
    max-width: 100%;
  }

  .applications .responsive-table .table-row {
    box-shadow: none;
  }

  .app-buttons {
    max-width: 95%;
    margin: 0px auto;
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
  }

  .other-apps-button {
    margin-top: 0;
  }
}
</style>
